import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BarChartIcon from '@mui/icons-material/BarChart';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ELEVATION } from '@openx/components-core/lib/theme/elevation.ts';
import { palette } from '@openx/components-core/lib/theme/paletteV2.ts';
import type { InventoryBundle, InventoryDomain } from '@openx/types/gqlTypes.ts';
import { abbreviateNumericValue } from '@openx/utils/lib/abbreviateNumericValue.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TooltipIcon } from '../../components/TooltipIcon/TooltipIcon.tsx';
import { spendTooltip } from '../../constants.ts';
import { useDealPrompt } from '../../hooks/useDealPrompt.ts';
import { calculateMetricsDeviation } from '../../utils/calculateMetricsDeviation.ts';
import type { MetricKey } from '../../utils/types.ts';
import { metricsOrder } from './consts.ts';
import { formatValue } from './utils.ts';

type DealPromptDetailsMetricsSectionProps = {
  items: InventoryDomain[] | InventoryBundle[];
};

export const DealPromptDetailsMetricsSection = ({ items }: DealPromptDetailsMetricsSectionProps) => {
  const { t } = useTranslation();
  const {
    state: { selectedItems },
  } = useDealPrompt();

  const LABELS: Record<MetricKey, string> = {
    totalBidSpendInUsd: t('30d Spend'),
    relevance: t('Relevance'),
    totalCprMarketRequests: t('Market Requests'),
    totalImpressions: t('Impressions'),
    avgBidCpm: t('Avg CPM'),
    avgViewability: t('Avg Viewability'),
    avgPubWinRate: t('Bid Rate'),
    avgOxWinRate: t('Win Rate'),
  };

  const { deviations, selectedMetrics, allMetrics } =
    useMemo(() => calculateMetricsDeviation(items, selectedItems), [items, selectedItems]) || {};

  return (
    <Box
      sx={{
        p: 4,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: 'flex-end',
          mb: 1.5,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
            borderRadius: '50px',
            px: 1.5,
            py: 0.5,
            backgroundColor: 'background.boxDarkest',
            color: 'primary.contrastText',
          }}
        >
          <BarChartIcon />

          <Typography variant="body2Bold">{t('Monthly Data')}</Typography>
        </Stack>
      </Stack>

      <Grid
        container
        spacing={4}
        sx={{
          display: 'flex',
          width: '100%',
          flexWrap: {
            xs: 'wrap',
            xl: 'nowrap',
          },
        }}
      >
        {metricsOrder.map(key => {
          const raw = selectedMetrics?.[key] ?? allMetrics?.[key];
          const value = typeof raw === 'number' ? formatValue(key, raw) : '—';

          const deviationRaw = deviations?.[key];
          const deviation = `${abbreviateNumericValue(deviationRaw, { decimalPlaces: 2 })}%`;
          const isDeviationNegative = (deviationRaw || 0) < 0;
          const DeviationIcon = isDeviationNegative ? ArrowDownwardIcon : ArrowUpwardIcon;

          return (
            <Card
              key={key}
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '4px',
                boxShadow: ELEVATION.elevation0,
                height: '112px',
                minWidth: {
                  xs: '144px',
                  xl: 'auto',
                },
                flex: {
                  xs: '0 0 144px',
                  xl: '1 1 0%',
                },
              }}
              data-test={`details-metric-${key}`}
            >
              <CardContent sx={{ p: 1.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {LABELS[key]}
                  {key === 'totalBidSpendInUsd' && (
                    <TooltipIcon data-test="spend-tooltip-icon" title={t(spendTooltip)} />
                  )}
                </Typography>

                <Typography variant="h1" component="p">
                  {value}
                </Typography>

                {!!deviationRaw && (
                  <Stack
                    direction="row"
                    spacing={0.25}
                    sx={{
                      alignItems: 'center',
                      color: isDeviationNegative ? palette.error.main : palette.success.dark,
                    }}
                  >
                    <DeviationIcon
                      data-test={`deviation-icon-${key}`}
                      sx={{ fontSize: 18 }}
                      aria-label={isDeviationNegative ? 'Decrease' : 'Increase'}
                    />

                    <Typography data-test={`deviation-metric-${key}`} variant="body1">
                      {deviation}
                    </Typography>
                  </Stack>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Grid>
    </Box>
  );
};
